"use client";

import React, { useState } from 'react';
import Image from 'next/image';
import { cn } from '@/shared/utils/tw';
import type { ConsentConfig } from '../types/consent';
import { DEFAULT_CONSENT_CONFIG } from '../const/consent-config';
import { TermsModal } from './terms-modal';
import type { LegalDocType } from '../const/legal-content';

interface BackgroundConfig {
  type: 'color' | 'image';
  value: string;
}

interface ExtendedConsentConfig extends ConsentConfig {
  background?: string | BackgroundConfig;
  image?: string;
  videoUrl?: string;
}

interface ConsentModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onDecline?: () => void;
  config?: Partial<ExtendedConsentConfig>;
  className?: string;
  isPreview?: boolean;
  isInitializing?: boolean;
  isAvatarLoading?: boolean;
  isGreetingLoading?: boolean;
}

/**
 * 동의 모달 컴포넌트
 *
 * 책임:
 * - 마이크 사용 동의 UI 표시
 * - 확인/거부 액션 처리
 * - 로딩 상태 표시
 */
export const ConsentModal: React.FC<ConsentModalProps> = ({
  isOpen,
  onConfirm,
  onDecline,
  config = {},
  className,
  isPreview = false,
  isInitializing = false,
  isAvatarLoading = false,
  isGreetingLoading = false,
}) => {
  // 기본값과 병합하여 확장된 설정 생성
  const extendedConfig: ExtendedConsentConfig = {
    ...DEFAULT_CONSENT_CONFIG,
    background: 'linear-gradient(180deg, #1a1a1a 0%, #000000 100%)',
    ...config,
  };

  const { title, background, image, videoUrl } = extendedConfig;

  // 약관 동의 체크박스 상태
  const [isAgreed, setIsAgreed] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [openedLegalDoc, setOpenedLegalDoc] = useState<LegalDocType | null>(null);

  // 비활성 버튼 클릭 시 안내
  const handleDisabledClick = () => {
    if (buttonsDisabled) {
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 2500);
    }
  };

  // 비디오 로딩 상태
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  // 배경 스타일 결정 (color 또는 image)
  const getBackgroundStyle = (): React.CSSProperties => {
    if (typeof background === 'string') {
      return { background };
    }
    if (background && typeof background === 'object' && background.type === 'image') {
      return { backgroundImage: `url(${background.value})`, backgroundSize: 'cover' };
    }
    if (background && typeof background === 'object') {
      return { background: background.value };
    }
    return { background: 'linear-gradient(180deg, #1a1a1a 0%, #000000 100%)' };
  };

  // 통합 로딩 상태 계산 (KioskCoverModal과 동일한 패턴)
  const showOverlayLoading = isInitializing || isAvatarLoading || isGreetingLoading;

  const buttonsDisabled = !isAgreed || isInitializing;

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        isPreview ? 'absolute' : 'fixed',
        'w-full flex items-end justify-center z-[200]',
        isPreview ? 'h-full' : 'h-dvh',
        'transition-opacity duration-500',
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        className
      )}
      style={getBackgroundStyle()}
    >
      <div className='w-full h-full relative flex flex-col items-center'>
        {/* 배경 비디오 또는 이미지 */}
        {videoUrl && !videoError ? (
          <video
            autoPlay loop muted playsInline
            onCanPlay={() => setIsVideoLoaded(true)}
            onError={() => { setVideoError(true); setIsVideoLoaded(true); }}
            className={cn(
              'absolute z-0 inset-0 w-full h-full object-cover',
              'transition-opacity duration-500',
              isVideoLoaded ? 'opacity-100' : 'opacity-0'
            )}
          >
            <source src={videoUrl} type="video/mp4" />
            <source src={videoUrl} type="video/webm" />
          </video>
        ) : image ? (
          <Image
            className='absolute z-0 top-0 left-0 object-contain w-[100%] aspect-[0.5855]'
            src={image}
            alt='background'
            fill
            priority
            draggable={false}
          />
        ) : null}

        {/* 로딩 오버레이 */}
        <div
          className={cn(
            "absolute inset-0 z-20 flex items-center justify-center bg-black/40 backdrop-blur-sm",
            "transition-opacity duration-300",
            showOverlayLoading ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
        >
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4" />
            <p className="text-white/80 text-lg font-pretendard">
              {isAvatarLoading ? '아바타 로딩 중...' : isGreetingLoading ? '음성 준비 중...' : '초기화 중...'}
            </p>
          </div>
        </div>

        {/* 상단: 라벨 + 타이틀 */}
        <div
          className={cn(
            'flex flex-col items-center gap-[6px] z-[1] pt-[40px]',
            'transition-opacity duration-300',
            showOverlayLoading ? 'opacity-0' : 'opacity-100'
          )}
        >
          <div
            className='flex items-center justify-center px-[10px] py-[4px] rounded-[16px] backdrop-blur-[10px]'
            style={{ background: 'rgba(62, 41, 57, 0.8)' }}
          >
            <span className='font-pretendard font-semibold text-[18px] text-white leading-[1.4]'>
              시범 운영 중
            </span>
          </div>
          <h3
            className='text-white text-center font-extrabold leading-[1.3] font-pretendard'
            style={{
              fontSize: '32px',
              letterSpacing: '-0.64px',
              textShadow: '0 0 6.73px rgba(0, 0, 0, 0.1)',
            }}
          >
            {title}
          </h3>
        </div>

        {/* 하단 그라디언트 */}
        <div
          className='absolute bottom-0 left-0 w-full z-[1] pointer-events-none'
          style={{
            height: '293px',
            background: 'linear-gradient(0deg, rgba(255, 255, 255, 0.8) 40%, rgba(255, 255, 255, 0) 100%)',
          }}
        />

        {/* 하단 컨텐츠: 버튼 + 동의 */}
        <div
          className={cn(
            'absolute bottom-0 left-0 w-full z-[2] flex flex-col items-center',
            'transition-opacity duration-300',
            showOverlayLoading ? 'opacity-0' : 'opacity-100'
          )}
          style={{ paddingBottom: '24px' }}
        >
          {/* 이용약관 안내 토스트 (Figma: 3158:4148) */}
          <div
            className={cn(
              'flex items-center justify-center gap-[4px] px-[16px] py-[10px] rounded-[16px]',
              'backdrop-blur-[4px] font-pretendard font-medium text-[16px] text-white text-center leading-[1.4] whitespace-nowrap',
              'transition-all duration-300',
              showAlert
                ? 'opacity-100'
                : 'opacity-0 pointer-events-none'
            )}
            style={{ background: 'rgba(62, 41, 57, 0.8)', marginBottom: '14px' }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
              <path d="M9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM9 15C5.685 15 3 12.315 3 9C3 5.685 5.685 3 9 3C12.315 3 15 5.685 15 9C15 12.315 12.315 15 9 15ZM8.25 12.75H9.75V8.25H8.25V12.75ZM8.25 6.75H9.75V5.25H8.25V6.75Z" fill="#BD9BB5"/>
            </svg>
            아래 이용약관에 동의해 주세요.
          </div>

          {/* 두 개 버튼 */}
          <div className='flex flex-col gap-[12px] w-full max-w-[318px] px-[0px]'>
            {/* 아바타와 대화하기 */}
            <button
              type="button"
              onClick={buttonsDisabled ? handleDisabledClick : onConfirm}
              className={cn(
                'w-full flex items-center justify-between px-[24px] py-[18px] rounded-[20px]',
                'backdrop-blur-[8px] border-2 border-solid border-transparent',
                'transition-all duration-500',
                buttonsDisabled
                  ? 'cursor-not-allowed'
                  : 'cursor-pointer active:scale-[0.98]'
              )}
              style={{
                borderColor: buttonsDisabled ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 0, 106, 0.26)',
                background: buttonsDisabled
                  ? 'rgba(255, 255, 255, 0.8)'
                  : 'linear-gradient(88deg, rgba(255, 255, 255, 0) 50%, rgba(255, 0, 106, 0.1) 100%), #FFF',
                boxShadow: buttonsDisabled ? 'none' : '0px 16px 24px 0px rgba(0, 0, 0, 0.1)',
              }}
            >
              <div className='flex items-center gap-[16px]'>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip_people_speak)">
                    <path d="M19.833 4.40757C19.833 4.40757 20.9997 6.7409 19.833 9.59276M23.4626 2.3335C23.4626 2.3335 25.796 6.5335 23.4626 11.6668" stroke={buttonsDisabled ? 'rgba(10,19,35,0.3)' : 'black'} strokeWidth="2.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="11.668" cy="7" r="3.5" stroke={buttonsDisabled ? 'rgba(10,19,35,0.3)' : 'black'} strokeWidth="2.33333"/>
                    <path d="M19.8337 23.3332H3.50033C2.21166 23.3332 1.15487 22.2654 1.57673 21.0478C2.51619 18.3362 5.25772 15.1665 11.667 15.1665C18.0763 15.1665 20.8178 18.3362 21.7572 21.0478C22.1791 22.2654 21.1223 23.3332 19.8337 23.3332Z" stroke={buttonsDisabled ? 'rgba(10,19,35,0.3)' : 'black'} strokeWidth="2.33333"/>
                  </g>
                  <defs>
                    <clipPath id="clip_people_speak">
                      <rect width="28" height="28" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                <span className={cn(
                  'font-pretendard font-semibold text-[18px] leading-[1.3]',
                  buttonsDisabled ? 'text-[rgba(10,19,35,0.3)]' : 'text-[#0A1323]'
                )}>
                  아바타와 대화하기
                </span>
              </div>
              <svg width="7" height="14" viewBox="0 0 7 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L6 7L1 13" stroke={buttonsDisabled ? 'rgba(10,19,35,0.3)' : '#0A1323'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* 문자로 상담하기 */}
            <button
              type="button"
              onClick={buttonsDisabled ? handleDisabledClick : onDecline}
              className={cn(
                'w-full flex items-center justify-between px-[24px] py-[18px] rounded-[20px]',
                'backdrop-blur-[8px] border-2 border-solid border-transparent',
                'transition-all duration-500',
                buttonsDisabled
                  ? 'cursor-not-allowed'
                  : 'cursor-pointer active:scale-[0.98]'
              )}
              style={{
                borderColor: 'rgba(255, 255, 255, 0.8)',
                background: buttonsDisabled ? 'rgba(255, 255, 255, 0.2)' : undefined,
                backgroundImage: buttonsDisabled
                  ? undefined
                  : 'linear-gradient(90deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(90deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.2) 100%)',
              }}
            >
              <div className='flex items-center gap-[16px]'>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 4.6665L14 3.49984H14L14 4.6665ZM24.5 13.9995H25.6667V13.9995L24.5 13.9995ZM3.5 13.9995L2.33333 13.9995V13.9995H3.5ZM5.63909 20.344L4.69029 19.6651L5.63909 20.344ZM5.5998 19.5985L6.49094 18.8455L5.5998 19.5985ZM4.16027 22.4107L3.21147 21.7318L4.16027 22.4107ZM13.9987 23.3332L14.8237 22.5083L13.9987 23.3332ZM13.9989 23.333L14.5207 22.2895L13.9989 23.333ZM14.0001 23.3335L14.0001 24.5002L14.0001 23.3335ZM13.9999 23.3335L14.5217 22.29L13.9999 23.3335ZM14 4.6665L14 5.83317C19.2873 5.83323 23.3332 9.6148 23.3333 13.9996L24.5 13.9995L25.6667 13.9995C25.6664 8.07529 20.31 3.49991 14 3.49984L14 4.6665ZM24.5 13.9995H23.3333C23.3333 18.3845 19.2874 22.1667 14.0001 22.1668L14.0001 23.3335L14.0001 24.5002C20.3103 24.5 25.6667 19.9237 25.6667 13.9995H24.5ZM13.9999 23.3335L14.5217 22.29L14.5207 22.2895L13.9989 23.333L13.4772 24.3765L13.4782 24.377L13.9999 23.3335ZM13.9986 23.3335V22.1668H4.63467V23.3335V24.5002H13.9986V23.3335ZM4.16027 22.4107L5.10907 23.0896L6.58789 21.0229L5.63909 20.344L4.69029 19.6651L3.21147 21.7318L4.16027 22.4107ZM5.5998 19.5985L6.49094 18.8455C5.33517 17.4777 4.66667 15.8014 4.66667 13.9995H3.5H2.33333C2.33333 16.3994 3.22841 18.5997 4.70866 20.3515L5.5998 19.5985ZM3.5 13.9995L4.66667 13.9996C4.66684 9.61485 8.71273 5.83329 14 5.83317L14 4.6665L14 3.49984C7.6901 3.49997 2.33356 8.07531 2.33333 13.9995L3.5 13.9995ZM5.63909 20.344L6.58789 21.0229C7.08015 20.3349 6.99225 19.4388 6.49094 18.8455L5.5998 19.5985L4.70866 20.3515C4.56591 20.1826 4.5213 19.9012 4.69029 19.6651L5.63909 20.344ZM4.63467 23.3335V22.1668C5.10936 22.1668 5.38529 22.7036 5.10907 23.0896L4.16027 22.4107L3.21147 21.7318C2.3828 22.89 3.21061 24.5002 4.63467 24.5002V23.3335ZM13.9987 23.3332L13.1738 24.1582C12.4389 23.4233 12.9594 22.1668 13.9986 22.1668V23.3335V24.5002C15.0382 24.5002 15.5588 23.2433 14.8237 22.5083L13.9987 23.3332ZM13.9989 23.333L14.5207 22.2895C13.3368 21.6975 12.2378 23.2222 13.1738 24.1582L13.9987 23.3332L14.8237 22.5083C15.7594 23.444 14.6608 24.9683 13.4772 24.3765L13.9989 23.333ZM14.0001 23.3335L14.0001 22.1668C14.1801 22.1668 14.3588 22.2085 14.5217 22.29L13.9999 23.3335L13.4782 24.377C13.6412 24.4585 13.82 24.5002 14.0001 24.5002L14.0001 23.3335Z" fill={buttonsDisabled ? 'rgba(10,19,35,0.3)' : '#0A1323'}/>
                </svg>
                <span className={cn(
                  'font-pretendard font-semibold text-[18px] leading-[1.3]',
                  buttonsDisabled ? 'text-[rgba(10,19,35,0.3)]' : 'text-[#0A1323]'
                )}>
                  문자로 상담하기
                </span>
              </div>
              <svg width="7" height="14" viewBox="0 0 7 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L6 7L1 13" stroke={buttonsDisabled ? 'rgba(10,19,35,0.3)' : '#0A1323'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* 동의 영역 */}
          <div className='flex flex-col items-center gap-[4px] mt-[24px] w-[266px]'>
            {/* 마이크 권한 안내 문구 */}
            <p className='text-[#62666E] text-center text-[14px] font-semibold leading-[1.4] font-pretendard w-full'>
              대화 상담을 위해 마이크 접근 권한이 필요합니다.
            </p>

            {/* 약관 동의 체크박스 */}
            <button
              type="button"
              onClick={() => setIsAgreed(!isAgreed)}
              className='flex flex-row items-center justify-center gap-[8px] cursor-pointer'
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
                <rect x="0.5" y="0.5" width="15" height="15" rx="3.5" fill={isAgreed ? 'rgba(62,41,57,0.8)' : 'rgba(255,255,255,0.1)'} stroke={isAgreed ? 'rgba(62,41,57,0.8)' : '#62666E'}/>
                {isAgreed && (
                  <path d="M4 8L7 11L12 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                )}
              </svg>
              <p className='text-[#62666E] text-[12px] font-medium text-left leading-[1.3]'>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenedLegalDoc('terms');
                  }}
                  className='underline underline-offset-2'
                >
                  이용약관
                </span>
                {' 및 '}
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenedLegalDoc('privacy');
                  }}
                  className='underline underline-offset-2'
                >
                  개인정보 처리방침
                </span>
                에 동의합니다.
              </p>
            </button>
          </div>
        </div>
      </div>

      {/* 이용약관 / 개인정보 처리방침 모달 */}
      {openedLegalDoc && (
        <TermsModal type={openedLegalDoc} onClose={() => setOpenedLegalDoc(null)} />
      )}
    </div>
  );
};

export default ConsentModal;
